import { xsrfCookieName, xsrfHeaderName } from '@/constants/common'
import urlManager from '@/urlManager'
import convertStringToBoolean from '@/utils/convertStringToBoolean'
import { camelCaseToSnakeCase, snakeCaseToCamelCase } from '@/utils/objectCasingConvertors'
import axios from 'axios'

function processOtypes(object, processors) {
  if (object === null) {
    return object
  } else if (object instanceof Array) {
    return object.map(item => processOtypes(item, processors))
  } else if (typeof object === 'object') {
    let newObject = {}
    Object.keys(object).forEach(key => {
      newObject[key] = processOtypes(object[key], processors)
    })
    if (object.otype && processors[object.otype]) {
      return processors[object.otype](newObject)
    } else {
      return newObject
    }
  } else {
    return object
  }
}

let processOwnerships = thing => {
  if (thing.ownerships) {
    let primaryOwnership = thing.ownerships.find(ownership => ownership.isPrimary)
    thing.ownerId = primaryOwnership ? primaryOwnership.userId : null
    let secondaryOwnerships = thing.ownerships.filter(ownership => !ownership.isPrimary)
    thing.coOwnerIds = secondaryOwnerships.map(ownership => ownership.userId)
  }
}

let OtypeProcessors = {
  user(user) {
    user.fullName = [user.firstName, user.lastName]
      .filter(name => name !== null && name !== '')
      .join(' ')
    user.displayName = user.fullName || user.email || ''
    return user
  },
  goal(goal) {
    processOwnerships(goal)
    return goal
  },
  key_result(kr) {
    processOwnerships(kr)
    return kr
  },
  initiative(initiative) {
    processOwnerships(initiative)
    return initiative
  },
  idea(idea) {
    processOwnerships(idea)
    return idea
  }
}

const apiClient = axios.create({
  baseURL: '/api',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  },
  xsrfCookieName,
  xsrfHeaderName,
  transformRequest: [
    data => {
      if (data) {
        if (data instanceof FormData || typeof data === 'string') {
          return data
        } else {
          return JSON.stringify(camelCaseToSnakeCase(data))
        }
      }
    }
  ],
  transformResponse: [
    (data, headers) => {
      let contentType = headers && headers['content-type']
      if (contentType && contentType.startsWith('application/json')) {
        return snakeCaseToCamelCase(JSON.parse(data))
      } else {
        return data
      }
    },
    data => {
      return processOtypes(data, OtypeProcessors)
    }
  ]
})

let errorHandler = null

apiClient.interceptors.response.use(
  response => response,
  error => {
    if (errorHandler) {
      errorHandler(error)
    }
    return Promise.reject(error)
  }
)

export default {
  client: apiClient,
  setErrorHandler(fn) {
    errorHandler = fn
  },
  async login(email, password) {
    const response = await apiClient.post(
      urlManager.login,
      {
        email,
        password
      },
      {
        params: { organizations: true, teams: true, email: true }
      }
    )
    return response.data.user
  },
  async logout() {
    await apiClient.post(urlManager.logout)
    return null
  },
  async forgotPassword(email) {
    await apiClient.post(urlManager.forgotPassword, { email: email })
  },
  async resetPassword(email, token, password) {
    await apiClient.post(urlManager.resetPassword, {
      email: email,
      token: token,
      password: password
    })
  },
  async getCurrentUser() {
    const response = await apiClient.get(urlManager.user, {
      params: { organizations: true, teams: true }
    })
    return response.data
  },
  async getUser(id) {
    const response = await apiClient.get(urlManager.userDetails(id))
    return response.data
  },
  async updateProfile(userInfo) {
    const response = await apiClient.patch(urlManager.user, userInfo)
    return response.data
  },
  async uploadProfilePic(blob) {
    let formData = new FormData()
    formData.append('file', blob)
    const response = await apiClient.post(urlManager.userPhoto, formData)
    return response.data
  },
  async getOrg(id) {
    const response = await apiClient.get(urlManager.orgDetails(id))
    return response.data
  },
  async getTeam(orgId, teamId) {
    const response = await apiClient.get(urlManager.team({ orgId, teamId }))
    return response.data
  },
  async getTeamMemberships(orgId, teamId) {
    const response = await apiClient.get(urlManager.teamMemberships({ orgId, teamId }))
    return response.data
  },
  async getGoals(
    orgId,
    teamId,
    {
      intervalStart,
      intervalEnd,
      intervalSubset,
      status,
      ownerId,
      primaryOwnerId,
      alignment,
      parentId,
      childId,
      isArchived,
      isExplicitlyArchived,
      scopeId,
      lastCheckIn,
      krStatus,
      expand,
      isFollowing,
      orderBy
    }
  ) {
    const response = await apiClient.get(urlManager.goals(orgId), {
      params: {
        team_id: teamId ? teamId : null,
        interval_start: intervalStart || null,
        interval_end: intervalEnd || null,
        interval_subset: intervalSubset || null,
        status: status || null,
        owner_id: ownerId || null,
        primary_owner_id: primaryOwnerId,
        alignment: alignment,
        parent_id: parentId,
        child_id: childId,
        is_archived: isArchived,
        is_explicitly_archived: isExplicitlyArchived,
        scope_id: scopeId,
        last_checkin: lastCheckIn,
        kr_status: krStatus,
        expand,
        is_following: convertStringToBoolean(isFollowing),
        order_by: orderBy || 'rank~id'
      }
    })
    return response.data
  },
  async getGoal(orgId, goalId, params) {
    const response = await apiClient.get(urlManager.goalDetail({ orgId, goalId }), { params })
    return response.data
  },
  async createNewGoal({
    orgId,
    teamId,
    title,
    description,
    ownerships,
    timeline,
    parentId,
    checkinFrequency,
    keyResults
  }) {
    const response = await apiClient.post(urlManager.goals(orgId), {
      title,
      teamId,
      description,
      ownerships,
      timeline,
      parentId,
      checkinFrequency,
      keyResults
    })
    return response.data
  },
  async updateGoal(goal) {
    let url = urlManager.goalDetail({ orgId: goal.orgId, goalId: goal.id })
    const response = await apiClient.put(url, goal)
    return response.data
  },
  async deleteGoal(goal) {
    await apiClient.delete(urlManager.goalDetail({ orgId: goal.organizationId, goalId: goal.id }))
  },
  async createEvent(orgId, goalId, event) {
    const response = await apiClient.post(urlManager.goalEvents({ orgId, goalId }), event)
    return response.data
  }
}
